import { useState, type ChangeEvent } from 'react';
import type { ImportStockReponse } from '@genviz/shared/dto';

import { useImportStock } from '../api/queries';
import { Bouton } from '../components/Boutons';
import { Carte } from '../components/Carte';
import { ZoneTexte } from '../components/Champs';
import { MessageErreur, texteErreur } from '../components/MessageErreur';
import { useNotifications } from '../components/Notifications';
import { PlancheQr, type EtiquetteQr } from '../components/QrCode';
import { Tableau, type Colonne } from '../components/Tableau';
import { LIBELLE_ERREUR, LIBELLE_MODELE, VALEUR_ABSENTE } from '../i18n/fr';
import {
  analyserCsvStock,
  EXEMPLE_MODELE_CSV,
  type AnalyseCsv,
  type LigneAnalysee,
} from '../lib/csv';
import { horodatageFichier, telechargerTexte } from '../lib/divers';
import { formaterNombre } from '../lib/format';
import type { CodeErreur } from '@genviz/shared/errors';

/**
 * Entrée en stock d'un lot d'appareils.
 *
 * L'écran suit l'ordre du geste réel : on dépose le fichier de production, on REGARDE ce qui a
 * été compris, on simule, on importe, on imprime. Le contrôle est fait dans le navigateur avant
 * tout appel réseau — un fichier de 500 lignes dont la colonne « modele » est mal orthographiée
 * doit être corrigé en trente secondes, pas découvert ligne à ligne dans une réponse d'erreur.
 *
 * La simulation (`simulation: true`) est proposée en premier et sans risque : elle passe par le
 * serveur, qui seul connaît les doublons du parc entier, mais n'écrit rien.
 */
export function ImportStockPage(): JSX.Element {
  const notifications = useNotifications();
  const importer = useImportStock();

  const [texteBrut, setTexteBrut] = useState('');
  const [analyse, setAnalyse] = useState<AnalyseCsv | null>(null);
  const [nomFichier, setNomFichier] = useState<string | null>(null);
  const [resultat, setResultat] = useState<ImportStockReponse | null>(null);
  const [plancheOuverte, setPlancheOuverte] = useState(false);

  const analyser = (contenu: string, fichier: string | null): void => {
    setTexteBrut(contenu);
    setNomFichier(fichier);
    setResultat(null);
    setAnalyse(analyserCsvStock(contenu));
  };

  const surFichier = (e: ChangeEvent<HTMLInputElement>): void => {
    const fichier = e.currentTarget.files?.[0];
    if (!fichier) return;
    void fichier.text().then((contenu) => {
      analyser(contenu, fichier.name);
    });
  };

  const lignesValides = analyse?.lignes.filter((l) => l.erreurs.length === 0) ?? [];
  const lignesFautives = analyse?.lignes.filter((l) => l.erreurs.length > 0) ?? [];

  const envoyer = (simulation: boolean): void => {
    if (lignesValides.length === 0) return;
    importer.mutate(
      { lignes: lignesValides.map((l) => l.valeurs), simulation },
      {
        onSuccess: (reponse) => {
          setResultat(reponse);
          if (reponse.simulation) {
            notifications.info(
              `Simulation : ${reponse.lignes_acceptees} ligne(s) seraient acceptées, ${reponse.lignes_rejetees.length} rejetée(s).`,
            );
          } else {
            notifications.succes(`${reponse.lignes_acceptees} appareil(s) entrés en stock.`);
          }
        },
        onError: (erreur) => {
          notifications.erreur(texteErreur(erreur));
        },
      },
    );
  };

  // Étiquettes des lignes RÉELLEMENT acceptées, avec leur secret : c'est le seul instant où il
  // est connu en clair.
  const rejetes = new Set((resultat?.lignes_rejetees ?? []).map((r) => r.numero_serie_electrificateur));
  const etiquettes: EtiquetteQr[] =
    resultat && !resultat.simulation
      ? lignesValides
          .filter((l) => !rejetes.has(l.valeurs.numero_serie_electrificateur))
          .map((l) => ({
            numeroSerie: l.valeurs.numero_serie_electrificateur,
            secret: l.valeurs.secret_appairage,
            complement: `${LIBELLE_MODELE[l.valeurs.modele]}${l.valeurs.lot ? ` · ${l.valeurs.lot}` : ''}`,
          }))
      : [];

  const colonnes: Array<Colonne<LigneAnalysee>> = [
    { cle: 'numero', entete: '#', rendu: (l) => l.numero, numerique: true },
    {
      cle: 'sn',
      entete: 'N° de série',
      rendu: (l) => <span className="gv-mono">{l.valeurs.numero_serie_electrificateur || VALEUR_ABSENTE}</span>,
    },
    {
      cle: 'module',
      entete: 'Module',
      rendu: (l) => <span className="gv-mono gv-secondaire">{l.valeurs.numero_serie_module || VALEUR_ABSENTE}</span>,
    },
    { cle: 'modele', entete: 'Modèle', rendu: (l) => LIBELLE_MODELE[l.valeurs.modele] },
    {
      cle: 'secret',
      entete: 'Clé',
      rendu: (l) => <span className="gv-mono">{l.valeurs.secret_appairage || VALEUR_ABSENTE}</span>,
    },
    { cle: 'lot', entete: 'Lot', rendu: (l) => l.valeurs.lot ?? VALEUR_ABSENTE },
    {
      cle: 'controle',
      entete: 'Contrôle',
      rendu: (l) =>
        l.erreurs.length === 0 ? (
          <span className="gv-badge gv-badge--vert">
            <span className="gv-badge__point" aria-hidden="true" />
            Valide
          </span>
        ) : (
          <div className="gv-pile gv-pile--serree">
            {l.erreurs.map((e) => (
              <span key={`${e.champ}-${e.message}`} className="gv-corps-s" style={{ color: 'var(--gv-rouge)' }}>
                <b className="gv-mono">{e.champ}</b> — {e.message}
              </span>
            ))}
          </div>
        ),
    },
  ];

  return (
    <>
      <header className="gv-entete">
        <div className="gv-pile gv-pile--serree">
          <h1 className="gv-h1">Import d’un lot</h1>
          <span className="gv-corps-s gv-secondaire">
            Entrée en stock des appareils sortis de production. Sans cette étape, aucun appareil ne
            peut être appairé par un client.
          </span>
        </div>
        <div className="gv-entete__actions">
          <Bouton
            onClick={() => {
              telechargerTexte(`genviz-modele-import-${horodatageFichier()}.csv`, EXEMPLE_MODELE_CSV);
            }}
          >
            Télécharger le modèle CSV
          </Bouton>
        </div>
      </header>

      <Carte titre="1. Fichier de production" sousTitre="CSV séparé par point-virgule, virgule ou tabulation">
        <div className="gv-pile">
          <input
            type="file"
            accept=".csv,text/csv,text/plain"
            className="gv-saisie"
            onChange={surFichier}
            aria-label="Fichier CSV à importer"
          />

          <ZoneTexte
            label="…ou coller le contenu directement"
            aide="Utile pour rejouer quelques lignes corrigées sans reconstruire un fichier."
            value={texteBrut}
            rows={6}
            onChange={(e) => {
              analyser(e.currentTarget.value, null);
            }}
          />

          {nomFichier ? (
            <span className="gv-corps-s gv-secondaire">
              Fichier chargé : <b>{nomFichier}</b>
            </span>
          ) : null}
        </div>
      </Carte>

      {analyse ? (
        <>
          {analyse.erreurGlobale ? (
            <div className="gv-bandeau gv-bandeau--erreur">{analyse.erreurGlobale}</div>
          ) : null}

          {analyse.colonnesInconnues.length > 0 ? (
            <div className="gv-bandeau gv-bandeau--attention">
              Colonnes ignorées car inconnues du contrat :{' '}
              <b className="gv-mono">{analyse.colonnesInconnues.join(', ')}</b>
            </div>
          ) : null}

          <Carte
            titre="2. Contrôle du fichier"
            sousTitre={`${formaterNombre(analyse.lignes.length)} ligne(s) lue(s) · séparateur « ${
              analyse.separateur === '\t' ? 'tabulation' : analyse.separateur
            } »`}
            actions={
              <>
                <span className="gv-badge gv-badge--vert">
                  <span className="gv-badge__point" aria-hidden="true" />
                  {formaterNombre(lignesValides.length)} valide(s)
                </span>
                {lignesFautives.length > 0 ? (
                  <span className="gv-badge gv-badge--rouge">
                    <span className="gv-badge__point" aria-hidden="true" />
                    {formaterNombre(lignesFautives.length)} en erreur
                  </span>
                ) : null}
              </>
            }
            sansPadding
          >
            <Tableau
              colonnes={colonnes}
              lignes={analyse.lignes}
              cleDe={(l) => String(l.numero)}
              titreVide="Aucune ligne de données"
            />
          </Carte>

          <Carte titre="3. Import">
            <div className="gv-pile">
              <p className="gv-corps-s gv-secondaire">
                La simulation interroge le serveur sans rien écrire : elle détecte les doublons avec
                le stock existant, que le contrôle local ne peut pas voir.
              </p>

              {importer.error ? <MessageErreur erreur={importer.error} /> : null}

              <div className="gv-ligne">
                <Bouton
                  disabled={lignesValides.length === 0}
                  occupe={importer.isPending}
                  onClick={() => {
                    envoyer(true);
                  }}
                >
                  Simuler
                </Bouton>
                <Bouton
                  variante="primaire"
                  disabled={lignesValides.length === 0}
                  occupe={importer.isPending}
                  onClick={() => {
                    envoyer(false);
                  }}
                >
                  Importer {formaterNombre(lignesValides.length)} ligne(s)
                </Bouton>
                {lignesFautives.length > 0 ? (
                  <span className="gv-corps-s gv-secondaire">
                    Les {formaterNombre(lignesFautives.length)} ligne(s) en erreur ne sont pas
                    envoyées.
                  </span>
                ) : null}
              </div>
            </div>
          </Carte>
        </>
      ) : null}

      {resultat ? (
        <Carte
          titre={resultat.simulation ? 'Résultat de la simulation' : 'Résultat de l’import'}
          sousTitre={`Import ${resultat.import_id}`}
          actions={
            !resultat.simulation && etiquettes.length > 0 ? (
              <Bouton
                variante="primaire"
                onClick={() => {
                  setPlancheOuverte(true);
                }}
              >
                Générer la planche de QR
              </Bouton>
            ) : null
          }
        >
          <div className="gv-pile">
            <div
              className={`gv-bandeau ${
                resultat.lignes_rejetees.length === 0 ? 'gv-bandeau--succes' : 'gv-bandeau--attention'
              }`}
            >
              {formaterNombre(resultat.lignes_acceptees)} ligne(s) acceptée(s) sur{' '}
              {formaterNombre(resultat.lignes_soumises)}.{' '}
              {resultat.simulation ? 'Aucune écriture n’a été faite.' : ''}
            </div>

            {resultat.lignes_rejetees.length > 0 ? (
              <div className="gv-table-cadre">
                <table className="gv-table">
                  <thead>
                    <tr>
                      <th>Ligne</th>
                      <th>N° de série</th>
                      <th>Champ</th>
                      <th>Cause</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultat.lignes_rejetees.map((r) => (
                      <tr key={`${r.ligne}-${r.code}`}>
                        <td className="gv-table__num">{r.ligne}</td>
                        <td className="gv-mono">{r.numero_serie_electrificateur ?? VALEUR_ABSENTE}</td>
                        <td className="gv-mono">{r.champ ?? VALEUR_ABSENTE}</td>
                        <td>{LIBELLE_ERREUR[r.code as CodeErreur] ?? r.code}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {!resultat.simulation && etiquettes.length > 0 ? (
              <p className="gv-corps-s gv-secondaire">
                Les clés d’appairage des {formaterNombre(etiquettes.length)} appareils importés ne
                seront plus jamais affichées : imprimez la planche maintenant.
              </p>
            ) : null}
          </div>
        </Carte>
      ) : null}

      <PlancheQr
        ouverte={plancheOuverte}
        surFermeture={() => {
          setPlancheOuverte(false);
        }}
        etiquettes={etiquettes}
      />
    </>
  );
}
